import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { MapPin, Clock, Phone, Globe } from 'lucide-react';
import { clubInfo } from '../mock';
import { useSiteImages } from '../context/SiteImagesContext';
import { useSiteConfig } from '../context/SiteConfigContext';
import { fetchTeam, fetchTrainingGroups, fetchTrainingLocations, resolveImg } from '../api';
import { usePageMeta } from '../hooks/usePageMeta';

const OurClub = () => {
  usePageMeta({ title: 'Notre Club - GR La Saline', description: 'Découvrez GR La Saline : notre équipe, nos groupes d\'entraînement et nos lieux à La Réunion.' });
  const images = useSiteImages();
  const config = useSiteConfig();
  const clubDescription = config?.content?.clubDescription || clubInfo.description;
  const [team, setTeam] = useState(clubInfo.team);
  const [trainingGroups, setTrainingGroups] = useState(clubInfo.trainingGroups);
  const [locations, setLocations] = useState(clubInfo.locations);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  useEffect(() => {
    fetchTeam().then((d) => { if (d && d.length) setTeam([...d].sort((a,b)=>(a.order||0)-(b.order||0))); }).catch(() => {});
    fetchTrainingGroups().then((d) => { if (d && d.length) setTrainingGroups([...d].sort((a,b)=>(a.order||0)-(b.order||0))); }).catch(() => {});
    fetchTrainingLocations().then((d) => { if (d && d.length) setLocations(d); }).catch(() => {});
  }, []);

  return (
    <div className="bg-black min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={images.ourclub_hero}
            alt="Notre Club"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/60 to-black" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block px-4 py-2 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-full text-fuchsia-300 text-sm font-medium tracking-wider mb-6"
          >
            À propos
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-5xl md:text-7xl font-bold text-white mb-6"
          >
            Notre <span className="bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">Club</span>
          </motion.h1>
        </div>
      </section>

      {/* Description Section */}
      <section className="relative py-24" ref={ref}>
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.7 }}
            className="max-w-5xl mx-auto mb-16"
          >
            <div className="relative rounded-3xl overflow-hidden aspect-[21/9]">
              <img src={images.ourclub_intro || images.ourclub_hero} alt="Notre club" className="w-full h-full object-cover" data-testid="ourclub-intro-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center mb-24"
          >
            <p className="text-lg md:text-xl text-white/70 leading-relaxed whitespace-pre-line">
              {clubDescription}
            </p>
          </motion.div>

          {/* Club Formateur FFGYM section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="max-w-5xl mx-auto mb-24"
            data-testid="club-formateur-section"
          >
            <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 md:gap-12 items-center bg-white/5 border border-white/10 rounded-3xl p-8 md:p-12">
              <img
                src="/api/uploads/club-formateur-ffgym.png"
                alt="Label Club Formateur FFGYM - GR La Saline"
                className="w-48 md:w-56 h-auto mx-auto md:mx-0"
                data-testid="club-formateur-logo"
              />
              <div>
                <h2 className="text-2xl md:text-4xl font-bold text-white mb-4 leading-tight">
                  CLUB FORMATEUR de la <span className="bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">FFGYM</span>
                </h2>
                <p className="text-white/70 leading-relaxed">
                  Notre club détient depuis maintenant 6 ans le label <strong className="text-white">Club Formateur</strong> décerné par la FFGYM.
                  Un club formateur GR labellisé par la Fédération Française de Gymnastique est une structure reconnue pour son engagement dans le <strong className="text-white">Parcours de Performance Fédéral (PPF)</strong>.
                  Ces clubs assurent une formation de qualité respectant le développement des jeunes talents en vue du haut niveau.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Team Section */}
          <div className="mb-32">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-5xl font-bold text-white text-center mb-12"
            >
              Notre <span className="bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">Équipe</span>
            </motion.h2>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 max-w-5xl mx-auto">
              {team.map((member, index) => (
                <motion.div
                  key={member.id || index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -10 }}
                  className="p-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:border-fuchsia-500/50 transition-all duration-500 text-center"
                >
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-fuchsia-500/20 to-pink-500/20 flex items-center justify-center mx-auto mb-3 overflow-hidden">
                    {member.photo ? (
                      <img src={resolveImg(member.photo)} alt={member.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-fuchsia-300 font-bold text-2xl">{(member.name || '').charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <h3 className="text-sm font-bold text-white mb-1">{member.name}</h3>
                  <p className="text-fuchsia-400 text-xs">{member.role}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Training Groups Section */}
          <div className="mb-32">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-5xl font-bold text-white text-center mb-4"
            >
              Nos <span className="bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">Cours</span>
            </motion.h2>
            <p className="text-white/60 text-center mb-16 max-w-2xl mx-auto">
              Des cours adaptés à tous les âges et tous les niveaux, de 5 à 18 ans
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {trainingGroups.map((group, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  className="relative group p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:border-fuchsia-500/50 transition-all duration-500"
                >
                  <div className="absolute top-8 right-8 text-7xl font-bold text-white/5">
                    0{index + 1}
                  </div>
                  
                  <div className="relative z-10">
                    <h3 className="text-2xl font-bold text-white mb-2">{group.name}</h3>
                    <p className="text-fuchsia-400 font-semibold mb-4">{group.ageRange}</p>
                    <p className="text-white/70 mb-4">{group.description}</p>
                    <div className="flex items-center gap-2 text-white/60 mb-2">
                      <Clock size={16} />
                      <span className="text-sm">{group.schedule}</span>
                    </div>
                    {group.locations && group.locations.length > 0 && (
                      <div className="flex items-start gap-2 text-white/60">
                        <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{group.locations.join(' • ')}</span>
                      </div>
                    )}
                  </div>

                  <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
                </motion.div>
              ))}
            </div>
          </div>

          {/* Locations Section */}
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-5xl font-bold text-white text-center mb-4"
            >
              Nos <span className="bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">Lieux</span> d'Entraînement
            </motion.h2>
            <p className="text-white/60 text-center mb-16 max-w-2xl mx-auto">
              Plusieurs sites pour vous accueillir dans toute la région
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {locations.map((location, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -10 }}
                  className={`p-6 bg-white/5 backdrop-blur-sm border rounded-2xl transition-all duration-500 ${
                    location.isMain
                      ? 'border-fuchsia-500/50 bg-gradient-to-br from-fuchsia-500/10 to-pink-500/10'
                      : 'border-white/10 hover:border-fuchsia-500/30'
                  }`}
                >
                  {location.isMain && (
                    <span className="inline-block px-3 py-1 bg-fuchsia-500 text-white text-xs font-bold rounded-full mb-4">
                      PRINCIPAL
                    </span>
                  )}
                  <div className="flex items-start gap-3 mb-4">
                    <MapPin className="text-fuchsia-400 flex-shrink-0 mt-1" size={20} />
                    <div>
                      <h3 className="text-lg font-bold text-white mb-1">{location.name}</h3>
                      <p className="text-white/60 text-sm">{location.address}</p>
                    </div>
                  </div>
                  {location.phone && (
                    <div className="flex items-center gap-2 text-white/60 text-sm mb-2">
                      <Phone size={14} />
                      <span>{location.phone}</span>
                    </div>
                  )}
                  {location.website && (
                    <div className="flex items-center gap-2 text-fuchsia-400 text-sm">
                      <Globe size={14} />
                      <a href={/^https?:\/\//i.test(location.website) ? location.website : `https://${location.website}`} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        {location.website}
                      </a>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurClub;
